import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ListTable, { ListTableActions, ListTableColumn } from '../../../shared/listView/ListTable';
import ListTableNameCell from '../../../shared/listView/ListTableNameCell';
import { formNamesService } from '../../../services/formNamesService';
import DeleteItemIcon from '../../../shared/DeleteItemIcon';
import RestoreItemIcon from '../../../shared/RestoreItemIcon';
import EditItemIcon from '../../../shared/EditItemIcon';
import { formDeleteService } from '../../../services/formDeleteService';
import { POPUP_KEY_DELETE_FORM, POPUP_KEY_RESTORE_FORM } from '../../../constant';
import useWindowDimensions from '../../../hooks/useWindowDimension';

type FormRow = {
    id: string;
    productForm: string;
    formGroup?: string;
    formType?: string;
    isDeleted?: boolean;
};

type FormTableProps = {
    items: FormRow[];
    isLoading?: boolean;
};

const FormTable = ({ items, isLoading = false }: FormTableProps) => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const { width } = useWindowDimensions();

    const columns = useMemo<ListTableColumn<FormRow>[]>(() => [
        {
            id: 'name',
            header: i18n.t('app.page.listView.name'),
            cell: (row) => (
                <ListTableNameCell itemCode={row} itemService={formNamesService} />
            ),
        },
        {
            id: 'code',
            header: i18n.t('app.page.listView.code'),
            cell: (row) => <span className="list-table__code">{row.productForm}</span>,
        },
        {
            id: 'group',
            header: i18n.t('app.page.product.addFormItem.formGroup'),
            cell: (row) => (
                row.formGroup
                    ? i18n.t(`product.scheme.formGroup.${row.formGroup}`)
                    : <span className="product__list-content_warning">{i18n.t('app.page.product.notAssigned')}</span>
            ),
        },
        {
            id: 'type',
            header: i18n.t('app.page.product.addFormItem.formType'),
            cell: (row) => (
                row.formType
                    ? i18n.t(`product.scheme.formType.${row.formType}`)
                    : <span className="product__list-content_warning">{i18n.t('app.page.product.notAssigned')}</span>
            ),
        },
        {
            id: 'status',
            header: i18n.t('app.page.listView.status'),
            className: 'list-table__td_status',
            cell: (row) => (
                row.isDeleted
                    ? <span className="list-table__chip">{i18n.t('app.page.listView.deleted')}</span>
                    : null
            ),
        },
        {
            id: 'actions',
            header: i18n.t('app.page.listView.actions'),
            className: 'list-table__td_actions',
            cell: (row) => (
                <ListTableActions>
                    {row.isDeleted ? (
                        <RestoreItemIcon itemId={row.id} popupKey={POPUP_KEY_RESTORE_FORM} />
                    ) : (
                        <EditItemIcon pageUrl={`/product/form/${row.id}/`} />
                    )}
                    <DeleteItemIcon
                        itemId={row.id}
                        itemDeleteService={formDeleteService}
                        popupKey={POPUP_KEY_DELETE_FORM}
                    />
                </ListTableActions>
            ),
        },
    ], [i18n]);

    return (
        <ListTable
            columns={columns}
            rows={items}
            rowKey={(row) => row.id}
            width={width}
            isLoading={isLoading}
            isRowDeleted={(row) => Boolean(row.isDeleted)}
            onRowClick={(row) => {
                if (!row.isDeleted) {
                    navigate(`/product/form/${row.id}/`);
                }
            }}
        />
    );
};

export default FormTable;
